import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/clerk-react";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../../../components/ui";
import googleIcon from "../../../assets/svg/auth/google.svg";
import { login, startGoogleLogin } from "../services/authService";
import { useRestoreFromRedirect } from "../hooks/useRestoreFromRedirect";
import { ApiError } from "../../../lib/http";
import { EXTERNAL_LINKS, ROUTES } from "../../../routes/paths";

const LoginPage = () => {
  const navigate = useNavigate();
  // Clerk headless: this screen stays the only UI. `signIn` is the in-flight
  // attempt object, which the OTP screen picks up again from the same hook.
  const { isLoaded, signIn } = useSignIn();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Validation states
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [apiError, setApiError] = useState("");

  // The Google button leaves for Google with `submitting` still set, since
  // nothing after the redirect runs. Coming back must hand the form over.
  useRestoreFromRedirect(() => setSubmitting(false));

  const validateEmail = (value: string): string => {
    if (!value) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setApiError("");
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    // An empty field that was never typed in is not wrong yet, it is just
    // unused. The field is focused on arrival and the Google button sits right
    // below it, so flagging it here would greet anyone taking that route with
    // an error about a form they were never going to fill in. Submitting still
    // asks for it.
    if (!email) {
      setEmailError("");
      return;
    }

    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubmit = async () => {
    const emailErr = validateEmail(email);

    setEmailError(emailErr);
    setEmailTouched(true);
    setApiError("");

    if (emailErr || !isLoaded || !signIn) {
      return;
    }

    setSubmitting(true);
    try {
      const { emailAddressId } = await login(signIn, email);
      navigate(ROUTES.verifyOtp, {
        state: { email, emailAddressId, flow: "login" },
      });
    } catch (error) {
      if (error instanceof ApiError) {
        setApiError(error.message);
      } else {
        setApiError(
          "Network error. Please check your connection and try again."
        );
      }
      setSubmitting(false);
      console.error("Log-in error:", error);
    }
  };

  // Leaves for Google's consent screen directly; Clerk comes back to
  // ROUTES.ssoCallback. Nothing after this runs unless the redirect fails.
  //
  // The "login" intent is what stops an unknown Google address from becoming a
  // new account: sign-up is the only route that creates one, because it is the
  // only route that asks for consent first.
  const handleGoogle = async () => {
    if (!isLoaded || !signIn) return;

    setApiError("");
    setSubmitting(true);
    try {
      await startGoogleLogin(signIn, ROUTES.dashboard);
    } catch (error) {
      setApiError(
        error instanceof ApiError
          ? error.message
          : "Could not start Google sign-in. Please try again."
      );
      setSubmitting(false);
      console.error("Google sign-in error:", error);
    }
  };

  const isFormValid = !validateEmail(email) && !submitting && isLoaded;

  // 16px on a phone, the design's 15px from sm up. iOS zooms the page into any
  // field it has to enlarge to read, and does not zoom back out when the field
  // is left - so a 15px input on a phone costs the user the rest of the screen.
  //
  // The phone field is taller and rounder than the designed one, and answers
  // focus with a ring as well as a border. It is white at every width.
  //
  // White because a filled field on this screen sat on the tail of the teal
  // wash behind it, and two tinted surfaces one on top of the other read as a
  // smudge rather than as a control. White is also what the field is holding:
  // the text the user types, which has the most contrast it can get against
  // the page. Focus then has the ring to move, and the border to turn teal,
  // which is the change a thumb that has just covered the field can still see.
  //
  // The border is the designed `#DCE0E4` at every width now - the lighter
  // `#E6EBEC` was an edge for a filled box, and is too faint to draw a white
  // one. Only the size, radius and ring are undone at `sm:`.
  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-4 border rounded-xl bg-white text-[16px] text-gray-900 placeholder-[#9AA4AE] focus:outline-none transition-all sm:px-4 sm:py-3.5 sm:rounded-lg sm:text-[15px] sm:focus:ring-0 ${
      hasError
        ? "bg-[#FEF6F6] border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/15"
        : "border-[#DCE0E4] focus:border-[#00C2CB] focus:ring-4 focus:ring-[#00C2CB]/15"
    }`;

  return (
    <AuthLayout phoneFill>
      {/* Bigger on a phone than the 24px the design draws for a desktop. A
          heading is sized against the column it heads, and that column is 460px
          on a desktop and the width of the glass on a phone - 24px reads as a
          heading in the first and as a line of body text in the second. */}
      <h1 className="text-[30px] leading-[1.15] font-semibold text-[#1F2937] sm:text-[24px] sm:leading-tight">
        Log in
      </h1>
      <p className="mt-2 text-[15px] text-[#62636C] sm:mt-1 sm:text-[14px]">
        Please enter your email to continue
      </p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          {/* Named on a phone, unnamed from sm up, where the design puts the
              placeholder alone in a field the eye reaches straight after the
              sentence above it. On a phone the field is further from that
              sentence and the placeholder disappears the moment a thumb starts
              typing, so the field says what it is above itself and keeps
              saying it. */}
          <label
            htmlFor="login-email"
            className="mb-2 block text-[13px] font-medium text-[#1E1F24] sm:hidden"
          >
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            disabled={submitting}
            autoComplete="email"
            autoFocus
            // A phone keyboard would otherwise capitalise and autocorrect its
            // way through an address. `go` labels its action key with what
            // this form actually does, since the field is the whole form.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            className={inputClasses(!!emailError && emailTouched)}
            placeholder="yourname@email.com"
          />
          {emailError && emailTouched && (
            <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
          )}
        </div>

        {apiError && <p className="text-xs text-red-500">{apiError}</p>}

        {/* Two phone-only states, both gone at sm where the design has neither.
            The lift is `enabled:` only: a coloured glow under a disabled button
            would light up the very control that is saying it cannot be pressed,
            so the shadow arrives with the button as the confirmation that there
            is now something to press. The hairline is the quieter half of the
            same idea: the disabled fill is a pale tint of the brand, which on
            a phone stands on the tail of this screen's teal wash - the wash
            stops above the button (see `AuthLayout`) so the two never sit on
            each other, and the edge is what makes sure of it. Both go once the
            button is live. */}
        <Button
          type="submit"
          variant="primary"
          size="touch"
          fullWidth
          loading={submitting}
          disabled={!isFormValid}
          className="mt-2 enabled:shadow-[0_10px_24px_-12px_rgba(0,194,203,0.9)] disabled:border disabled:border-[#B7EDF3] sm:disabled:border-0 sm:shadow-none"
        >
          Log in
        </Button>

        {/* <p className="text-center text-[13px] leading-relaxed text-[#62636C]">
          By logging in, you agree to our{" "}
          <a
            href={EXTERNAL_LINKS.termsOfUse}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00C2CB] font-medium hover:underline"
          >
            Terms of Use
          </a>
        </p> */}
      </form>

      <div className="my-6 sm:my-7 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#E3E7EA]" />
        <span className="text-sm font-medium text-[#9AA4AE]">OR</span>
        <span className="h-px flex-1 bg-[#E3E7EA]" />
      </div>

      <Button
        variant="secondary"
        size="touch"
        fullWidth
        onClick={handleGoogle}
        disabled={submitting || !isLoaded}
        startIcon={<img src={googleIcon} alt="" className="w-5 h-5" />}
      >
        Continue with Google
      </Button>

      {/* What is left of the screen once everything above it has been laid
          out, and nothing at all from sm up.

          Only the last line is pinned to the foot. Pushing the submit down
          there instead would put a few hundred pixels of nothing between a
          field and the button that sends it, which reads as a screen that has
          not finished loading rather than as one built for a thumb - the
          button belongs with the thing it acts on. The line underneath is the
          one element with no such tie: it is the way off this screen
          altogether, and the bottom edge is where a phone app keeps that.

          How far off that edge it stops is the shell's bottom padding, which
          has to clear the phone's own navigation bar - see `AuthLayout`.

          It collapses to nothing when the content already fills the screen, so
          a short handset just scrolls as it did. */}
      <div className="flex-1 sm:hidden" aria-hidden />

      <p className="mt-8 sm:mt-12 text-center text-[14px] text-[#3A4652]">
        Don&rsquo;t have an account?{" "}
        {/* The padding is what a thumb has to hit; the matching negative
            margin takes it back out of the line, so the sentence reads at the
            spacing it was drawn at. */}
        <button
          type="button"
          onClick={() => navigate(ROUTES.signup)}
          className="-m-2 p-2 text-[#00C2CB] font-medium hover:underline"
        >
          Sign up
        </button>
      </p>
    </AuthLayout>
  );
};

export default LoginPage;
